<!-- Hero Section -->
<section
  class="relative overflow-hidden py-12 sm:py-16 lg:py-24 transition-colors"
  [class.bg-cover]="hasCustomBackground"
  [class.bg-center]="hasCustomBackground"
  [style.background-image]="hasCustomBackground ? 'url(' + backgroundImageUrl + ')' : null"
  [ngClass]="hasCustomBackground ? 'text-white' : (theme === 'warm' ? 'bg-[#faf6f0] text-stone-900 border-b border-stone-200' : (theme === 'bold' ? 'bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 text-white' : 'bg-slate-900 text-white'))"
  aria-labelledby="hero-heading"
>
  <!-- Background Overlays -->
  @if (hasCustomBackground) {
    <!-- Semi-transparent dark overlay for custom background image readability -->
    <div class="absolute inset-0 bg-slate-950/75 backdrop-blur-[1px] pointer-events-none z-0"></div>
  } @else {
    @if (theme === 'modern') {
      <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-600/30 via-purple-600/20 to-transparent pointer-events-none"></div>
    } @else if (theme === 'warm') {
      <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-amber-200/30 via-stone-100/40 to-transparent pointer-events-none"></div>
    } @else {
      <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-600/40 via-red-600/20 to-transparent pointer-events-none"></div>
    }
  }

  <div class="container mx-auto px-4 sm:px-6 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-12 items-center">
      
      <!-- Left Hero Text Content -->
      <div class="lg:col-span-7 space-y-6 sm:space-y-8">
        <!-- Top Status Badge -->
        @if (showBadge) {
          <a
            [routerLink]="ctaLink"
            class="inline-flex max-w-full flex-wrap items-center gap-2 border px-3 py-1.5 sm:px-4 sm:py-2 rounded-2xl sm:rounded-full text-xs sm:text-sm font-medium transition-all group cursor-pointer"
            [ngClass]="hasCustomBackground ? 'bg-white/10 border-white/20 text-white hover:bg-white/20' : (theme === 'warm' ? 'bg-amber-500/15 border-amber-400/40 text-amber-900 hover:bg-amber-500/25' : (theme === 'bold' ? 'bg-red-600/20 border-red-500/60 text-red-300 font-bold hover:bg-red-600/30' : 'bg-blue-500/10 border-blue-400/30 text-blue-300 hover:bg-blue-500/20'))"
          >
            <span class="relative flex h-2 w-2 shrink-0">
              <span class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" [ngClass]="hasCustomBackground ? 'bg-emerald-400' : (theme === 'warm' ? 'bg-amber-500' : (theme === 'bold' ? 'bg-red-400' : 'bg-emerald-400'))"></span>
              <span class="relative inline-flex rounded-full h-2 w-2" [ngClass]="hasCustomBackground ? 'bg-emerald-500' : (theme === 'warm' ? 'bg-amber-600' : (theme === 'bold' ? 'bg-red-500' : 'bg-emerald-500'))"></span>
            </span>
            <span class="truncate max-w-[200px] sm:max-w-none">{{ badgeText }}</span>
            <span class="text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-semibold shrink-0" [ngClass]="hasCustomBackground ? 'bg-white/20 text-white' : (theme === 'warm' ? 'bg-amber-500/20 text-amber-900' : (theme === 'bold' ? 'bg-red-600/30 text-red-200' : 'bg-blue-500/20 text-blue-300 group-hover:bg-blue-500/40'))">Live Classes</span>
            <i class="fa-solid fa-arrow-right text-xs ml-0.5 shrink-0 transition-transform group-hover:translate-x-0.5"></i>
          </a>
        }

        <!-- Main Title -->
        <h1
          id="hero-heading"
          class="text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight break-words"
          [ngClass]="hasCustomBackground ? 'text-white' : (theme === 'warm' ? 'text-stone-900' : 'text-white')"
        >
          {{ heroTitle }}
        </h1>

        <!-- Subtitle Paragraph -->
        <p
          class="text-base sm:text-xl max-w-2xl leading-relaxed"
          [ngClass]="hasCustomBackground ? 'text-slate-200' : (theme === 'warm' ? 'text-stone-600' : 'text-slate-300')"
        >
          {{ heroSubtitle }}
        </p>

        <!-- CTAs -->
        <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 pt-2">
          <a
            [routerLink]="ctaLink"
            class="daisy-btn daisy-btn-md sm:daisy-btn-lg flex items-center justify-center gap-2 text-sm sm:text-base font-bold shadow-lg"
            [ngClass]="hasCustomBackground ? 'daisy-btn-primary bg-blue-600 hover:bg-blue-500 border-none text-white' : (theme === 'warm' ? 'daisy-btn-warning bg-amber-600 hover:bg-amber-500 border-none text-white shadow-amber-600/30' : (theme === 'bold' ? 'daisy-btn-error bg-red-600 hover:bg-red-500 border-none text-white shadow-red-600/40 font-bold' : 'daisy-btn-primary bg-blue-600 hover:bg-blue-500 border-none text-white shadow-blue-500/30'))"
          >
            <i class="fa-solid fa-video" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'text-white' : 'text-amber-300'"></i>
            <span>{{ ctaText }}</span>
          </a>

          @if (secondaryCtaText) {
            @if (isExternalLink(secondaryCtaLink)) {
              <a
                [href]="resolvedSecondaryCtaLink"
                target="_blank"
                rel="noopener noreferrer"
                class="daisy-btn daisy-btn-outline daisy-btn-md sm:daisy-btn-lg flex items-center justify-center gap-2 text-sm sm:text-base"
                [ngClass]="hasCustomBackground ? 'text-white border-white/40 hover:bg-white/10' : (theme === 'warm' ? 'text-stone-700 border-stone-400 hover:bg-stone-200' : 'text-slate-200 border-slate-700 hover:bg-slate-800 hover:text-white')"
              >
                <i [class]="secondaryCtaIcon" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'text-amber-600' : 'text-emerald-400'"></i>
                <span>{{ secondaryCtaText }}</span>
              </a>
            } @else {
              <a
                [routerLink]="secondaryCtaLink"
                class="daisy-btn daisy-btn-outline daisy-btn-md sm:daisy-btn-lg flex items-center justify-center gap-2 text-sm sm:text-base"
                [ngClass]="hasCustomBackground ? 'text-white border-white/40 hover:bg-white/10' : (theme === 'warm' ? 'text-stone-700 border-stone-400 hover:bg-stone-200' : 'text-slate-200 border-slate-700 hover:bg-slate-800 hover:text-white')"
              >
                <i [class]="secondaryCtaIcon" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'text-amber-600' : 'text-cyan-400'"></i>
                <span>{{ secondaryCtaText }}</span>
              </a>
            }
          } @else if (isUserLoggedIn) {
            <a
              routerLink="/my-learnings"
              class="daisy-btn daisy-btn-outline daisy-btn-md sm:daisy-btn-lg flex items-center justify-center gap-2 text-sm sm:text-base"
              [ngClass]="hasCustomBackground ? 'text-white border-white/40 hover:bg-white/10' : (theme === 'warm' ? 'text-stone-700 border-stone-400 hover:bg-stone-200' : 'text-slate-200 border-slate-700 hover:bg-slate-800 hover:text-white')"
            >
              <i class="fa-solid fa-bolt" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'text-amber-600' : 'text-cyan-400'"></i>
              <span>My Learnings</span>
            </a>
          } @else {
            <a
              routerLink="/auth/register"
              class="daisy-btn daisy-btn-outline daisy-btn-md sm:daisy-btn-lg flex items-center justify-center gap-2 text-sm sm:text-base"
              [ngClass]="hasCustomBackground ? 'text-white border-white/40 hover:bg-white/10' : (theme === 'warm' ? 'text-stone-700 border-stone-400 hover:bg-stone-200' : 'text-slate-200 border-slate-700 hover:bg-slate-800 hover:text-white')"
            >
              <i class="fa-solid fa-rocket" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'text-amber-600' : 'text-blue-400'"></i>
              <span>Get Started</span>
            </a>
          }
        </div>

        <!-- Trust & Outcome Metrics Strip -->
        @if (showMetrics && heroMetrics.length > 0) {
          <div
            class="pt-6 sm:pt-8 mt-6 sm:mt-8 border-t"
            [ngClass]="hasCustomBackground ? 'border-white/15' : (theme === 'warm' ? 'border-stone-200' : 'border-slate-800/80')"
          >
            <div
              class="grid gap-2.5 sm:gap-3.5"
              [ngClass]="heroMetrics.length === 2 ? 'grid-cols-1 sm:grid-cols-2' : (heroMetrics.length === 4 ? 'grid-cols-2 lg:grid-cols-4' : 'grid-cols-1 sm:grid-cols-3')"
            >
              @for (metric of heroMetrics; track (metric.icon + '-' + metric.title + '-' + $index)) {
                <div
                  class="group relative flex items-center gap-3 p-3 sm:p-3.5 rounded-2xl border transition-all duration-200 hover:-translate-y-0.5 shadow-sm overflow-hidden"
                  [ngClass]="hasCustomBackground
                    ? 'bg-white/10 hover:bg-white/15 border-white/20 text-white backdrop-blur-md shadow-black/20'
                    : (theme === 'warm'
                      ? 'bg-white/80 hover:bg-white border-stone-200/90 hover:border-amber-300/80 shadow-stone-200/50 hover:shadow-md backdrop-blur-sm'
                      : (theme === 'bold'
                        ? 'bg-slate-900/85 hover:bg-slate-900 border-blue-600/30 hover:border-blue-500/60 shadow-blue-950/40 backdrop-blur-md'
                        : 'bg-slate-900/60 hover:bg-slate-900/90 border-slate-800/90 hover:border-slate-700/90 shadow-slate-950/40 backdrop-blur-md'))"
                >
                  <!-- Metric Icon Container -->
                  <div
                    class="w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shrink-0 border transition-transform duration-200 group-hover:scale-105 shadow-inner"
                    [ngClass]="hasCustomBackground
                      ? 'bg-white/15 text-white border-white/25'
                      : (theme === 'warm'
                        ? 'bg-amber-50 text-amber-700 border-amber-200/60'
                        : (theme === 'bold'
                          ? 'bg-blue-600/20 text-blue-300 border-blue-400/30'
                          : 'bg-blue-500/15 text-blue-400 border-blue-500/25'))"
                  >
                    <i [class]="metric.icon + ' text-sm sm:text-base'"></i>
                  </div>

                  <!-- Metric Text -->
                  <div class="min-w-0 flex-1">
                    <p
                      class="text-xs sm:text-sm font-bold tracking-tight truncate leading-snug"
                      [ngClass]="hasCustomBackground
                        ? 'text-white'
                        : (theme === 'warm' ? 'text-stone-900' : 'text-white')"
                    >
                      {{ metric.title }}
                    </p>
                    <p
                      class="text-[11px] sm:text-xs truncate leading-normal"
                      [ngClass]="hasCustomBackground
                        ? 'text-slate-200/80'
                        : (theme === 'warm' ? 'text-stone-500' : 'text-slate-400')"
                    >
                      {{ metric.subtitle }}
                    </p>
                  </div>
                </div>
              }
            </div>
          </div>
        }
      </div>

      <!-- Right Column: Dynamic Visual Showcase -->
      <div class="lg:col-span-5 w-full">
        <div class="relative max-w-md mx-auto w-full">
          
          <!-- Mode A: Solo Tutor / Lead Instructor Portrait Card -->
          @if (mediaType === 'portrait') {
            <div
              class="rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl transition-all border relative w-full"
              [ngClass]="theme === 'warm' && !hasCustomBackground ? 'bg-white border-stone-200 shadow-stone-300/30 text-stone-900' : (theme === 'bold' ? 'bg-slate-900/95 border-blue-600/40 shadow-blue-950/60 text-white' : 'bg-slate-800/90 backdrop-blur-xl border-slate-700/70 shadow-slate-950/60 text-white')"
            >
              <!-- Portrait Image Container -->
              <div class="relative h-52 sm:h-64 w-full rounded-2xl overflow-hidden mb-4 sm:mb-5 bg-slate-950 shadow-inner">
                @if (instructorAvatar) {
                  <img
                    [src]="instructorAvatar"
                    [alt]="portraitName"
                    class="w-full h-full object-cover object-top"
                  />
                } @else {
                  <div class="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 text-white/50 space-y-2">
                    <i class="fa-solid fa-chalkboard-user text-5xl text-primary/40"></i>
                    <p class="text-xs text-white/60">Upload photo in Instructor section</p>
                  </div>
                }
                <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                
                <!-- Live Admissions Badge -->
                <div class="absolute top-3 right-3">
                  <span class="daisy-badge daisy-badge-sm daisy-badge-success text-white font-bold gap-1 shadow-md text-xs">
                    <span class="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span> {{ portraitTag }}
                  </span>
                </div>

                <!-- Instructor Title Banner Overlay -->
                <div class="absolute bottom-3 left-3 right-3 text-white">
                  <p class="text-base sm:text-lg font-bold truncate leading-tight">{{ portraitName }}</p>
                  <p class="text-xs text-white/80 truncate">{{ portraitTitle }}</p>
                </div>
              </div>

              <!-- Value Points -->
              <div class="space-y-2 text-xs">
                @for (highlight of portraitHighlights; track $index) {
                  <div class="flex items-start gap-2.5 p-2 rounded-xl" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'bg-stone-100 text-stone-800' : 'bg-white/5 text-slate-200'">
                    <i class="fa-solid fa-circle-check text-primary text-xs sm:text-sm mt-0.5 shrink-0"></i>
                    <span class="font-medium truncate">{{ highlight }}</span>
                  </div>
                }
              </div>

              <!-- Quick Enroll CTA -->
              <div class="mt-4 pt-3 border-t" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'border-stone-200' : 'border-slate-700/60'">
                <a
                  [routerLink]="ctaLink"
                  class="daisy-btn daisy-btn-sm w-full rounded-xl gap-2 font-bold"
                  [ngClass]="theme === 'warm' && !hasCustomBackground ? 'daisy-btn-warning bg-amber-600 text-white hover:bg-amber-500 border-none' : (theme === 'bold' ? 'daisy-btn-error bg-red-600 text-white hover:bg-red-500 border-none' : 'daisy-btn-primary bg-blue-600 text-white hover:bg-blue-500 border-none')"
                >
                  <i class="fa-solid fa-graduation-cap"></i>
                  <span>Explore Active Batches</span>
                </a>
              </div>

            </div>
          } @else if (mediaType === 'video') {
            <!-- Mode Video: Embedded Demo / Lesson Teaser Card -->
            <div
              class="rounded-2xl sm:rounded-3xl p-3 sm:p-5 shadow-2xl transition-all border relative w-full overflow-hidden"
              [ngClass]="theme === 'warm' && !hasCustomBackground ? 'bg-white border-stone-200 shadow-stone-300/30 text-stone-900' : (theme === 'bold' ? 'bg-slate-900/95 border-blue-600/40 shadow-blue-950/60 text-white' : 'bg-slate-800/90 backdrop-blur-xl border-slate-700/70 shadow-slate-950/60 text-white')"
            >
              <div class="flex items-center justify-between pb-3 border-b gap-2 text-xs" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'border-stone-200' : 'border-slate-700/60'">
                <div class="flex items-center gap-2 font-semibold">
                  <i class="fa-solid fa-circle-play text-red-500 text-sm"></i>
                  <span>Class Demo & Teaching Sample</span>
                </div>
                <span class="daisy-badge daisy-badge-xs daisy-badge-ghost font-mono">1080p HD</span>
              </div>

              <div class="relative w-full aspect-video rounded-xl overflow-hidden my-3 bg-black shadow-inner">
                @if (safeVideoEmbedUrl) {
                  <iframe
                    [src]="safeVideoEmbedUrl"
                    class="w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                  ></iframe>
                } @else {
                  <div class="w-full h-full flex flex-col items-center justify-center text-white/50 space-y-2 p-4 text-center">
                    <i class="fa-solid fa-video text-3xl text-primary/40"></i>
                    <p class="text-xs">Add a YouTube or Vimeo URL in Hero settings</p>
                  </div>
                }
              </div>

              <p class="text-xs text-base-content/70 leading-relaxed px-1">
                Experience our interactive teaching methodology and theory breakdown before enrolling.
              </p>
            </div>
          } @else if (mediaType === 'custom-image') {
            <!-- Mode Custom Image: Banner / Classroom Showcase -->
            <div
              class="rounded-2xl sm:rounded-3xl p-2.5 sm:p-3 shadow-2xl transition-all border relative w-full overflow-hidden"
              [ngClass]="theme === 'warm' && !hasCustomBackground ? 'bg-white border-stone-200 shadow-stone-300/30' : (theme === 'bold' ? 'bg-slate-900/95 border-blue-600/40 shadow-blue-950/60' : 'bg-slate-800/90 backdrop-blur-xl border-slate-700/70 shadow-slate-950/60')"
            >
              <div class="relative w-full h-64 sm:h-80 rounded-2xl overflow-hidden bg-slate-950 shadow-inner">
                @if (mediaCustomImageUrl) {
                  <img
                    [src]="mediaCustomImageUrl"
                    [alt]="heroTitle"
                    class="w-full h-full object-cover"
                  />
                } @else {
                  <div class="w-full h-full flex flex-col items-center justify-center text-white/50 space-y-2 p-4 text-center">
                    <i class="fa-solid fa-image text-4xl text-primary/40"></i>
                    <p class="text-xs">Add an image URL in Hero settings</p>
                  </div>
                }
              </div>
            </div>
          } @else {
            <!-- Mode Batch Card: Academy Schedule Hub Card -->
            <div
              class="rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl transition-all border relative w-full"
              [ngClass]="theme === 'warm' && !hasCustomBackground ? 'bg-white border-stone-200 shadow-stone-300/30 text-stone-900' : (theme === 'bold' ? 'bg-slate-900/95 border-blue-600/40 shadow-blue-950/60 text-white' : 'bg-slate-800/90 backdrop-blur-xl border-slate-700/70 shadow-slate-950/60 text-white')"
            >
              <!-- Academy Card Header -->
              <div
                class="flex items-center justify-between pb-4 border-b gap-2"
                [ngClass]="theme === 'warm' && !hasCustomBackground ? 'border-stone-200' : 'border-slate-700/60'"
              >
                <div class="flex items-center gap-2.5 sm:gap-3 min-w-0">
                  <div
                    class="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-md shrink-0"
                    [ngClass]="theme === 'warm' && !hasCustomBackground ? 'bg-amber-600' : (theme === 'bold' ? 'bg-gradient-to-tr from-blue-600 to-red-600' : 'bg-gradient-to-tr from-blue-500 to-indigo-600')"
                  >
                    <i class="fa-solid fa-graduation-cap"></i>
                  </div>
                  <div class="min-w-0">
                    <h3 class="font-bold text-sm sm:text-base truncate" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'text-stone-900' : 'text-white'">
                      {{ tenantName || 'Academy' }}
                    </h3>
                    <p class="text-xs flex items-center gap-1 text-emerald-500 truncate">
                      <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shrink-0"></span> Active Admissions
                    </p>
                  </div>
                </div>
                <span
                  class="daisy-badge daisy-badge-sm shrink-0"
                  [ngClass]="theme === 'warm' && !hasCustomBackground ? 'daisy-badge-warning font-semibold' : (theme === 'bold' ? 'daisy-badge-error font-bold text-white' : 'daisy-badge-primary daisy-badge-outline')"
                >Live Now</span>
              </div>

              <!-- Scheduled Batches Feature Block -->
              <a
                [routerLink]="ctaLink"
                class="block my-4 sm:my-5 rounded-2xl p-3.5 sm:p-4 border space-y-2.5 sm:space-y-3 transition-all group cursor-pointer"
                [ngClass]="theme === 'warm' && !hasCustomBackground ? 'bg-stone-50 border-stone-200 hover:border-amber-400' : 'bg-slate-900/90 border-slate-800 hover:border-blue-500/50'"
              >
                <div class="flex items-center justify-between text-xs gap-2" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'text-stone-500' : 'text-slate-400'">
                  <span class="truncate"><i class="fa-solid fa-clock mr-1" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'text-amber-600' : 'text-blue-400'"></i> Scheduled Live Batches</span>
                  <span class="font-semibold flex items-center gap-1 shrink-0" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'text-amber-700' : 'text-blue-400'">Enroll Today <i class="fa-solid fa-chevron-right text-[10px] transition-transform group-hover:translate-x-0.5"></i></span>
                </div>
                <p class="font-bold text-xs sm:text-sm transition-colors line-clamp-2" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'text-stone-900 group-hover:text-amber-700' : 'text-slate-100 group-hover:text-blue-300'">
                  Comprehensive Theory & Exam Paper Preparation
                </p>
                <div class="flex items-center justify-between text-xs pt-1 gap-2">
                  <span class="truncate text-[11px]" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'text-stone-500' : 'text-slate-400'">
                    <i class="fa-solid fa-shield-halved mr-1 text-emerald-500"></i> Verified Material
                  </span>
                  <span class="daisy-badge daisy-badge-sm shrink-0" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'daisy-badge-ghost text-stone-600' : 'daisy-badge-secondary text-[10px]'">Zoom Live</span>
                </div>
              </a>

              <!-- Quick Metrics -->
              <div class="grid grid-cols-2 gap-2 sm:gap-3 pt-1">
                <div
                  class="rounded-xl p-2.5 sm:p-3 border text-center"
                  [ngClass]="theme === 'warm' && !hasCustomBackground ? 'bg-stone-50 border-stone-200' : 'bg-slate-900/60 border-slate-800'"
                >
                  <p class="text-[11px] sm:text-xs" [ngClass]="theme === 'warm' ? 'text-stone-500' : 'text-slate-400'">Class Replays</p>
                  <p class="text-base sm:text-lg font-bold" [ngClass]="theme === 'warm' && !hasCustomBackground ? 'text-amber-700' : 'text-blue-400'">24/7 Access</p>
                </div>
                <div
                  class="rounded-xl p-2.5 sm:p-3 border text-center"
                  [ngClass]="theme === 'warm' && !hasCustomBackground ? 'bg-stone-50 border-stone-200' : 'bg-slate-900/60 border-slate-800'"
                >
                  <p class="text-[11px] sm:text-xs" [ngClass]="theme === 'warm' ? 'text-stone-500' : 'text-slate-400'">Satisfaction</p>
                  <p class="text-base sm:text-lg font-bold text-amber-500">4.9 ★</p>
                </div>
              </div>

              <!-- Decorative Floating Badge -->
              <div
                class="absolute -bottom-5 -right-5 rounded-2xl p-3.5 shadow-xl flex items-center gap-3 hidden md:flex border"
                [ngClass]="theme === 'warm' && !hasCustomBackground ? 'bg-amber-600 text-white border-amber-500' : (theme === 'bold' ? 'bg-blue-600 text-white border-blue-400' : 'bg-blue-600 text-white border-blue-400/40')"
              >
                <div class="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-lg">
                  <i class="fa-solid fa-book-open"></i>
                </div>
                <div>
                  <p class="text-[10px] opacity-90 uppercase font-semibold">Study Packs</p>
                  <p class="text-xs font-bold">Comprehensive Notes Included</p>
                </div>
              </div>

            </div>
          }

        </div>
      </div>

    </div>
  </div>
</section>
